/**
 * Pagina /tesoreria — Dashboard del tesorero (Sprint 17 Fase 2).
 *
 * Resumen del mes + sparklines + tendencia % + chart año calendario
 * "Ingresos vs gastos" + donut "Gastos por categoria" + feed de
 * actividad reciente.
 *
 * Vista año calendario (C2): los charts grandes muestran 12 meses
 * Ene-Dic del año en curso; las sparklines muestran enero al mes
 * actual.
 *
 * Arquitectura View + wrapper:
 * - TesoreriaHomeView: componente puro reusable desde preview dev.
 * - TesoreriaHome: wrapper que orquesta los 5 fetch.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listPagos } from '../../lib/api/pagos';
import { listSolicitudesConDetalle } from '../../lib/api/solicitudes-recurso';
import type { PagoRead } from '../../lib/api/types';
import {
  getActividadReciente,
  getComparativaAnual,
  getDashboardResumen,
  getGastosPorCategoria,
  getSparklines,
  type ActividadRecienteResponse,
  type ComparativaAnualResponse,
  type DashboardTesoreriaResumen,
  type GastosPorCategoriaResponse,
  type SparklinesResponse,
} from '../../lib/api/dashboard-tesoreria';
import { formatCLP, formatFecha } from '../../lib/format';
import { hoy } from '../../lib/tiempo';
import { Card } from '../../components/ui/Card';
import { Monto, Num } from '../../components/tesoreria/Monto';
import {
  Sparkline,
  TrendPill,
} from '../../components/tesoreria/Sparkline';
import { BarChartIngresosVsGastos } from '../../components/tesoreria/BarChartIngresosVsGastos';
import { DonutChartGastos } from '../../components/tesoreria/DonutChartGastos';
import { FeedActividad } from '../../components/tesoreria/FeedActividad';

const NOMBRE_MES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

// Nombre del mes a partir de un ISO YYYY-MM-DD. Lo usa el rótulo del donut
// "Gastos por categoría" (que sigue siendo mensual; el toggle Año/Mes es C6).
function nombreMes(isoFecha: string): string {
  const [, mm] = isoFecha.split('-');
  const idx = Math.max(0, Math.min(11, Number(mm) - 1));
  return NOMBRE_MES[idx]!;
}

// Rótulo corto mes+año (Ene'26) para los ejes de la sparkline (C1).
// Recibe un ISO YYYY-MM-DD.
function mesAnioCorto(isoFecha: string): string {
  const [yyyy] = isoFecha.split('-');
  const abbr = nombreMes(isoFecha).slice(0, 3);
  const cap = abbr.charAt(0).toUpperCase() + abbr.slice(1);
  return `${cap}'${yyyy.slice(2)}`;
}

// C7: badge de estado en la lista "Casas con saldo en contra".
const ESTADO_CASA_LABEL: Record<string, string> = {
  atrasada: 'Atrasada',
  suspendida: 'Suspendida',
  al_dia_pendiente_semestre: 'Pend. semestre',
};
const ESTADO_CASA_COLOR: Record<string, string> = {
  atrasada: 'text-state-warning',
  suspendida: 'text-state-danger',
  al_dia_pendiente_semestre: 'text-state-info',
};

export interface TesoreriaHomeViewProps {
  resumen: DashboardTesoreriaResumen | null;
  sparklines: SparklinesResponse | null;
  comparativa: ComparativaAnualResponse | null;
  gastosCategoria: GastosPorCategoriaResponse | null;
  actividad: ActividadRecienteResponse | null;
  colaPagos: PagoRead[];
  // S17-15 — conteo de solicitudes de recurso esperando cobro.
  solicitudesEsperandoCobro: { count: number; masDe30Dias: number };
  loading: boolean;
  error: string | null;
  // C6 — toggle del donut "Gastos por categoría".
  donutScope: 'anio' | 'mes';
  onDonutScopeChange: (s: 'anio' | 'mes') => void;
}

export function TesoreriaHomeView({
  resumen,
  sparklines,
  comparativa,
  gastosCategoria,
  actividad,
  colaPagos,
  solicitudesEsperandoCobro,
  loading,
  error,
  donutScope,
  onDonutScopeChange,
}: TesoreriaHomeViewProps) {
  const anio = sparklines?.anio ?? new Date().getFullYear();
  // Rótulos de eje de las sparklines (C1): mes inicial → mes final del año.
  // Las 3 series comparten los mismos meses (enero → mes actual).
  const puntosMes = sparklines?.recaudacion ?? sparklines?.saldo_bancario ?? [];
  const leyendaIzq = puntosMes.length ? mesAnioCorto(puntosMes[0]!.mes) : '';
  const leyendaDer = puntosMes.length
    ? mesAnioCorto(puntosMes[puntosMes.length - 1]!.mes)
    : '';
  // C3/C4: tinte naranja de "alerta" solo cuando hay algo que atender
  // (count > 0); neutra si 0 o cargando. `!` para ganar al bg/border de Card.
  const cardAlerta = (count: number | undefined): string =>
    !loading && (count ?? 0) > 0
      ? '!bg-orange/10 !border-orange/30'
      : '';
  return (
    <div className="px-4 md:px-8 py-6 space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">
            Panel del tesorero
          </h1>
          <p className="text-sm text-text-secondary mt-1 max-w-2xl">
            Resumen del mes en curso y vista anual del año fiscal.
          </p>
        </div>
        <div className="text-xs text-text-muted">Año {anio}</div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Fila 1 — 3 cards financieras con sparkline + tendencia */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CardFinanciera
          label="Saldo bancario"
          monto={resumen?.saldo_bancario ?? 0}
          loading={loading}
          subtexto={
            (resumen?.saldo_bancario_disponible === false
              ? 'Sin registro reciente'
              : 'Último registro') +
            (resumen && resumen.total_depositos > 0
              ? ` · +${formatCLP(resumen.total_depositos)} en depósito`
              : '')
          }
          tendencia={resumen?.tendencia_saldo_pct ?? null}
          higherIsBetter
          serie={
            sparklines?.saldo_bancario.map((p) => p.monto) ?? []
          }
          color="#7ab8ff"
          leyendaIzq={leyendaIzq}
          leyendaDer={leyendaDer}
        />
        <CardFinanciera
          label={`Recaudación ${resumen?.anio_activo ?? ''}`}
          monto={resumen?.recaudacion_ytd ?? 0}
          loading={loading}
          subtexto="Acumulado del año"
          tendencia={resumen?.tendencia_recaudacion_pct ?? null}
          higherIsBetter
          serie={sparklines?.recaudacion.map((p) => p.monto) ?? []}
          color="#5fd9b1"
          leyendaIzq={leyendaIzq}
          leyendaDer={leyendaDer}
        />
        <CardFinanciera
          label={`Gastos ${resumen?.anio_activo ?? ''}`}
          monto={resumen?.gastos_ytd ?? 0}
          loading={loading}
          subtexto="Acumulado del año"
          tendencia={resumen?.tendencia_gastos_pct ?? null}
          higherIsBetter={false}
          serie={sparklines?.gastos.map((p) => p.monto) ?? []}
          color="#ff7a7a"
          leyendaIzq={leyendaIzq}
          leyendaDer={leyendaDer}
        />
      </div>

      {/* Fila 2 — alertas clickables */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/tesoreria/pagos"
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-orange rounded"
        >
          <Card className={cardAlerta(resumen?.pagos_sin_clasificar.count)}>
            <div className="p-4 hover:bg-bg-elevated transition-colors">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-medium text-text-primary">
                  Pagos sin clasificar
                </div>
                <div className="text-xs text-text-muted">Ir a Pagos →</div>
              </div>
              <div className="mt-3 flex items-baseline gap-3">
                <div className="text-3xl font-medium text-text-primary num">
                  {loading ? '—' : (resumen?.pagos_sin_clasificar.count ?? 0)}
                </div>
                <div className="text-xs text-text-secondary">
                  {loading ? '' : (
                    <>
                      <Monto value={resumen?.pagos_sin_clasificar.monto_total ?? 0} />{' '}
                      en cola
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Link>

        <Link
          to="/tesoreria/casas?filtro=con_saldo_contra"
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-orange rounded"
        >
          <Card className={cardAlerta(resumen?.casas_saldo_contra.count)}>
            <div className="p-4 hover:bg-bg-elevated transition-colors">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-medium text-text-primary">
                  Casas con saldo en contra
                </div>
                <div className="text-xs text-text-muted">Ir a Casas →</div>
              </div>
            <div className="mt-3 flex items-baseline gap-3">
              <div className="text-3xl font-medium text-text-primary num">
                {loading ? '—' : (resumen?.casas_saldo_contra.count ?? 0)}
              </div>
              <div className="text-xs text-text-secondary">
                {loading ? '' : (
                  <>
                    <Monto value={resumen?.casas_saldo_contra.monto_total ?? 0} />{' '}
                    atrasado
                  </>
                )}
              </div>
              {!loading && (
                <div className="ml-auto">
                  <TrendPill
                    pct={resumen?.tendencia_casas_contra_pct ?? null}
                    higherIsBetter={false}
                    title="Casas con cuotas vencidas vs mes anterior"
                  />
                </div>
              )}
            </div>
            </div>
          </Card>
        </Link>

        {/* S17-15 — Card solicitudes de recurso esperando cobro */}
        <Link
          to="/tesoreria/recursos?tab=solicitudes"
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-orange rounded"
        >
          <Card>
            <div className="p-4 hover:bg-bg-elevated transition-colors">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-medium text-text-primary">
                  Solicitudes esperando cobro
                </div>
                <div className="text-xs text-text-muted">
                  Ir a Solicitudes →
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-3">
                <div className="text-3xl font-medium text-text-primary num">
                  {loading ? '—' : solicitudesEsperandoCobro.count}
                </div>
                <div className="text-xs text-text-secondary">
                  {loading ? '' : solicitudesEsperandoCobro.masDe30Dias > 0 ? (
                    <>
                      {solicitudesEsperandoCobro.masDe30Dias} con más de 30 días
                    </>
                  ) : (
                    'al día'
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Fila 3 — Chart anual + Donut gastos por categoria */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="p-4">
            <div className="text-sm font-medium text-text-primary mb-2">
              Ingresos vs Gastos · {anio}
            </div>
            {loading || !comparativa ? (
              <div className="text-sm text-text-muted h-60 flex items-center">
                Cargando…
              </div>
            ) : (
              <BarChartIngresosVsGastos meses={comparativa.meses} />
            )}
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-baseline justify-between mb-3 gap-2">
              <div className="text-sm font-medium text-text-primary">
                Gastos por categoría
              </div>
              <div className="flex gap-1 text-xs flex-shrink-0">
                {(['anio', 'mes'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onDonutScopeChange(s)}
                    className={`px-2 py-0.5 rounded ${
                      donutScope === s
                        ? 'bg-orange/15 text-orange font-medium'
                        : 'text-text-muted hover:text-text-primary'
                    }`}
                  >
                    {s === 'anio'
                      ? `Año ${resumen?.anio_activo ?? ''}`.trim()
                      : `Mes${resumen ? ` · ${nombreMes(resumen.mes_actual)}` : ''}`}
                  </button>
                ))}
              </div>
            </div>
            {loading || !gastosCategoria ? (
              <div className="text-sm text-text-muted h-48 flex items-center">
                Cargando…
              </div>
            ) : (
              <DonutChartGastos
                items={gastosCategoria.items}
                total={gastosCategoria.total}
              />
            )}
          </div>
        </Card>
      </div>

      {/* Fila 4 — Pagos por distribuir + Casas atrasadas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-sm font-medium text-text-primary">
                Pagos por distribuir
              </div>
              <Link
                to="/tesoreria/pagos"
                className="text-xs text-orange hover:underline"
              >
                Ver todos →
              </Link>
            </div>
            {loading && (
              <div className="text-sm text-text-muted">Cargando…</div>
            )}
            {!loading && colaPagos.length === 0 && (
              <div className="text-sm text-text-muted">
                Sin pagos pendientes de clasificar.
              </div>
            )}
            {!loading && colaPagos.length > 0 && (
              <ul className="divide-y divide-white/[0.06]">
                {colaPagos.map((p) => (
                  <li key={p.id} className="py-2 flex items-center gap-3">
                    <div className="text-xs text-text-muted flex-shrink-0 w-20">
                      {formatFecha(p.fecha_transferencia)}
                    </div>
                    <div className="text-xs text-text-secondary flex-1 truncate font-mono">
                      {p.remitente_nombre || '(sin remitente)'}
                    </div>
                    <div className="text-sm text-text-primary text-right">
                      <Monto value={p.monto_total} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-sm font-medium text-text-primary">
                Casas con saldo en contra
              </div>
              <Link
                to="/tesoreria/casas?filtro=con_saldo_contra"
                className="text-xs text-orange hover:underline"
              >
                Ver casas →
              </Link>
            </div>
            {loading && (
              <div className="text-sm text-text-muted">Cargando…</div>
            )}
            {!loading &&
              (resumen?.casas_atrasadas_top5.length ?? 0) === 0 && (
                <div className="text-sm text-text-muted">
                  Ninguna casa con saldo en contra. ✓
                </div>
              )}
            {!loading &&
              resumen &&
              resumen.casas_atrasadas_top5.length > 0 && (
                <ul className="divide-y divide-white/[0.06]">
                  {resumen.casas_atrasadas_top5.map((c) => (
                    <li
                      key={c.lote_id}
                      className="py-2 flex items-center gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-text-primary font-medium">
                          Casa <Num>{c.identificador}</Num>
                          {c.titular && (
                            <span className="text-text-secondary font-normal">
                              {' '}
                              · {c.titular}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-text-muted mt-0.5">
                          {c.ultimo_pago_fecha
                            ? `Último pago: ${formatFecha(c.ultimo_pago_fecha)}`
                            : 'Sin pagos registrados'}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full bg-white/[0.04] ${
                            ESTADO_CASA_COLOR[c.estado] ?? 'text-text-muted'
                          }`}
                        >
                          {ESTADO_CASA_LABEL[c.estado] ?? c.estado}
                        </span>
                        <div className="text-sm text-red-400 text-right">
                          <Monto value={c.monto_adeudado} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </Card>
      </div>

      {/* Fila 5 — Actividad reciente full-width */}
      <Card>
        <div className="p-4">
          <div className="text-sm font-medium text-text-primary mb-3">
            Actividad reciente
          </div>
          <FeedActividad
            eventos={actividad?.eventos ?? []}
            loading={loading}
          />
        </div>
      </Card>
    </div>
  );
}

interface CardFinancieraProps {
  label: string;
  monto: number;
  subtexto: string;
  loading: boolean;
  tendencia: number | null;
  higherIsBetter: boolean;
  serie: number[];
  color: string;
  /** Rótulos de eje bajo la sparkline (C1): mes inicial → mes final del año. */
  leyendaIzq?: string;
  leyendaDer?: string;
}

function CardFinanciera({
  label,
  monto,
  subtexto,
  loading,
  tendencia,
  higherIsBetter,
  serie,
  color,
  leyendaIzq,
  leyendaDer,
}: CardFinancieraProps) {
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xs uppercase tracking-wide text-text-muted">
            {label}
          </div>
          {!loading && (
            <TrendPill pct={tendencia} higherIsBetter={higherIsBetter} />
          )}
        </div>
        <div className="text-2xl md:text-3xl font-medium text-text-primary mt-2">
          {loading ? '—' : <Monto value={monto} />}
        </div>
        <div className="text-xs text-text-secondary mt-1">{subtexto}</div>
        {!loading && (
          <div className="mt-3">
            <Sparkline
              data={serie}
              color={color}
              ariaLabel={label}
              fullWidth
              height={40}
            />
            {(leyendaIzq || leyendaDer) && (
              <div className="flex justify-between mt-1 text-[10px] text-text-muted tracking-wide">
                <span>{leyendaIzq}</span>
                <span>{leyendaDer}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export function TesoreriaHome() {
  const [resumen, setResumen] = useState<DashboardTesoreriaResumen | null>(
    null,
  );
  const [sparklines, setSparklines] = useState<SparklinesResponse | null>(
    null,
  );
  const [comparativa, setComparativa] =
    useState<ComparativaAnualResponse | null>(null);
  const [gastosCategoria, setGastosCategoria] =
    useState<GastosPorCategoriaResponse | null>(null);
  const [actividad, setActividad] =
    useState<ActividadRecienteResponse | null>(null);
  const [colaPagos, setColaPagos] = useState<PagoRead[]>([]);
  const [solicitudesEsperandoCobro, setSolicitudesEsperandoCobro] = useState<{
    count: number;
    masDe30Dias: number;
  }>({ count: 0, masDe30Dias: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // C6: alcance del donut "Gastos por categoría". Default "anio" (YTD).
  const [donutScope, setDonutScope] = useState<'anio' | 'mes'>('anio');

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [r, s, c, a, pagos, solicitudes] = await Promise.all([
          getDashboardResumen(),
          getSparklines(),
          getComparativaAnual(),
          getActividadReciente(10),
          listPagos({ estado: 'por_distribuir' }),
          listSolicitudesConDetalle({ estado: 'esperando_cobro' }),
        ]);
        if (cancel) return;
        setResumen(r);
        setSparklines(s);
        setComparativa(c);
        setActividad(a);
        setColaPagos(pagos.slice(0, 5));

        // S17-15: cuenta solicitudes con >30 días desde solicitado_at.
        const treintaDiasAtras = new Date();
        treintaDiasAtras.setDate(treintaDiasAtras.getDate() - 30);
        const limite = treintaDiasAtras.toISOString();
        const masDe30 = solicitudes.filter(
          (sol) => sol.solicitado_at < limite,
        ).length;
        setSolicitudesEsperandoCobro({
          count: solicitudes.length,
          masDe30Dias: masDe30,
        });
      } catch (e) {
        if (cancel) return;
        setError(e instanceof Error ? e.message : 'Error al cargar dashboard');
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => {
      cancel = true;
    };
  }, []);

  // C6: el donut se recarga al cambiar el toggle Año/Mes (independiente del
  // resto del dashboard). "anio" → YTD del año activo; "mes" → mes en curso.
  useEffect(() => {
    let cancel = false;
    setGastosCategoria(null);
    // Mes en curso en zona Santiago (helper), para coincidir con el backend
    // en el borde de fin de mes.
    const mesISO = hoy().slice(0, 7);
    const pedido =
      donutScope === 'mes'
        ? getGastosPorCategoria({ mes: mesISO })
        : getGastosPorCategoria();
    pedido
      .then((g) => {
        if (!cancel) setGastosCategoria(g);
      })
      .catch(() => {
        if (!cancel) setGastosCategoria(null);
      });
    return () => {
      cancel = true;
    };
  }, [donutScope]);

  return (
    <TesoreriaHomeView
      resumen={resumen}
      sparklines={sparklines}
      comparativa={comparativa}
      gastosCategoria={gastosCategoria}
      actividad={actividad}
      colaPagos={colaPagos}
      solicitudesEsperandoCobro={solicitudesEsperandoCobro}
      loading={loading}
      error={error}
      donutScope={donutScope}
      onDonutScopeChange={setDonutScope}
    />
  );
}
